'use client';

/* ──────────────────────────────────────────────
 *  CheckoutStep — finalisation en deux temps sur le même écran
 *  (spec Parcours P3 § 7.6, planche 2) : coordonnées et champs du
 *  pro, puis, une fois la pré-réservation créée, le paiement sous le
 *  formulaire replié. Un seul bouton selon le cas : « Continuer vers
 *  le paiement », « Confirmer le rendez-vous », « Envoyer ma demande »,
 *  puis « Payer 36,00 € » / « Payer l’acompte 10,80 € ». Validation
 *  par le pro : mention au-dessus du bouton ; case d'engagement
 *  seulement quand une pénalité existe. Le champ code et le récap des
 *  montants vivent dans le récap.
 * ────────────────────────────────────────────── */

import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { useBookingCodes } from '../../context/BookingCodesContext';
import { type StepNav, useRegisterStepNav } from '../../context/StepNavContext';
import { setPaymentRedirectPending } from '../../context/use-booking-funnel';
import type { AppliedCodes, BookingCallbacks, CustomFieldAnswer, PreReservationResponse } from '../../types';
import { answersForFields } from '../../utils/custom-fields';
import { cartHasLowerBoundPricing, hasNonZeroFormattedAmount, withFromPrefix } from '../../utils/payment-amounts';
import { resolvePromoErrorMessage } from '../../utils/promo-messages';
import { promotionPendingNotice, promotionRejectedNotice } from '../../utils/promotion-display';
import {
  type CheckoutCustomerDraft,
  EMAIL_RE,
  hasCustomerInfoErrors,
  isValidPhone,
  readCheckoutDraft,
  saveCheckoutDraft,
  validateCustomerInfo,
} from '../../utils/customer-form';
import {
  type CheckoutFlow,
  type CheckoutPayChoice,
  chargesOnlineNow,
  hasCancellationPenalty,
  needsNoOnlinePayment,
  offersPayChoice,
  paymentButtonLabel,
  resolveCheckoutFlow,
  validationNotice,
} from '../../utils/checkout-flow';
import { BookingPaymentSection } from '../shared/BookingPaymentSection';
import { BookingPaymentTimeline } from '../shared/BookingPaymentTimeline';
import { CountdownTimer } from '../shared/CountdownTimer';
import { useScrollToError } from '../shared/useScrollToError';
import { DynamicFormFields } from '../shared/DynamicFormFields';
import { useCustomFieldAnswers } from '../shared/useCustomFieldAnswers';
import { CustomerInfoFields, type CustomerInfoErrors } from '../shared/CustomerInfoFields';
import { BookingCodeField } from '../shared/BookingCodeField';
import { toSupplementSelectionPayload } from '../../utils/supplement-selection';

/** Créneau envoyé à la pré-réservation (contrat du callback `onPreReserve`). */
type PreReserveSlotPayload = Parameters<NonNullable<BookingCallbacks['onPreReserve']>>[0]['slots'][number];

/** Phrase serveur destinée au client (`PublicApiError.userMessage`), sinon rien. */
function getUserMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const maybe = (error as { userMessage?: unknown }).userMessage;
  return typeof maybe === 'string' && maybe.length > 0 ? maybe : undefined;
}

function getErrorKey(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const maybe = (error as { errorKey?: unknown }).errorKey;
  return typeof maybe === 'string' ? maybe : undefined;
}

/* Refus de la pré-réservation qui veut dire « ce créneau n'est plus libre » : retour aux créneaux. */
const SLOT_LOST_ERRORS = new Set(['PRE_RESERVATION_SLOT_NOT_AVAILABLE', 'PRE_RESERVATION_SLOT_WITHIN_MIN_NOTICE']);

const EMPTY_FORM: CheckoutCustomerDraft = { firstname: '', lastname: '', email: '', phone: '', note: '' };

/* ── Component ──────────────────────────────── */

export function CheckoutStep(): JSX.Element {
  const { ctx, send, callbacks, labels, locale, goBack, flagBaseUrl, professionalSlug } = useBooking();
  const codesApi = useBookingCodes();
  const {
    cartItems,
    selectedPlaceId,
    memberAssignments,
    resourceAssignments,
    selectedSlot,
    customerInfo,
    preReservationUuid,
    preReservationData,
    customFields,
    appliedCodes,
    cartSummary,
  } = ctx;
  /* Promotion sous condition client : annoncée avant le formulaire, vérifiée à sa validation ; si la
     condition tombe, la pré-réservation est créée au prix normal et le bandeau le dit (jamais bloquant). */
  const promotionPending = promotionPendingNotice(cartSummary, labels);
  const promotionRejected = promotionRejectedNotice(
    preReservationData?.promotion_rejected_reason,
    preReservationData?.promotion_rejected_label,
    labels,
  );

  /* ── Coordonnées : celles du tunnel, sinon le brouillon de la visite (créneau pris, retour au site) ── */
  const [form, setForm] = useState<CheckoutCustomerDraft>(
    (): CheckoutCustomerDraft => customerInfo ?? readCheckoutDraft(professionalSlug) ?? EMPTY_FORM,
  );
  const [formErrors, setFormErrors] = useState<CustomerInfoErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  useEffect((): void => saveCheckoutDraft(professionalSlug, form), [professionalSlug, form]);

  /* ── Custom field answers (hook partagé avec le tunnel course) ── */
  const cartPrestationIds = useMemo(
    (): number[] => cartItems.map((item): number => item.prestation.prestation_id),
    [cartItems],
  );
  /* Lieu réservé : la réservation se déroule en UN lieu (étape lieu, auto-assigné s'il est unique) —
     le MÊME `selectedPlaceId` que le `pro_place_id` de chaque créneau de `slotPayload`, donc le lieu de
     la réservation créée. Aucun lieu ⇒ aucun lieu réservé : un champ restreint à des lieux ne s'applique pas. */
  const cartPlaceIds = useMemo(
    (): number[] => (selectedPlaceId !== undefined ? [selectedPlaceId] : []),
    [selectedPlaceId],
  );
  const {
    visibleFields: visibleCustomFields,
    answers: customAnswers,
    errors: customErrors,
    updateAnswer: updateCustomAnswer,
    blurField: handleCustomFieldBlur,
    validate: validateCustomFields,
  } = useCustomFieldAnswers({
    fields: customFields,
    prestationIds: cartPrestationIds,
    placeIds: cartPlaceIds,
    requiredLabel: labels.required_field,
  });
  /* Réponses envoyées : celles des seuls champs APPLICABLES et renseignés (comme le tunnel course). */
  const customAnswersPayload = useMemo(
    (): CustomFieldAnswer[] => answersForFields(customAnswers, visibleCustomFields),
    [customAnswers, visibleCustomFields],
  );

  /* ── UI state ───────────────────────────────── */
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  /* L'action est dans la barre du bas, le message en fin de contenu : on l'amène dans l'écran (revue MI24). */
  const submitErrorRef = useScrollToError(submitError);
  const [cancellationAccepted, setCancellationAccepted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const [paymentRefreshToken, setPaymentRefreshToken] = useState(0);
  const [payChoice, setPayChoice] = useState<CheckoutPayChoice>('online');

  /* Shared loading: either the "pay on site" confirmation or the inline Stripe payment is in flight. */
  const busy = confirming || stripeProcessing;

  /* Confirmation Stripe en vol : un paiement hébergé (Klarna) décharge la page pour rediriger — ce n'est pas un
     abandon pour la mesure du parcours. Levée dès que la confirmation revient sans rediriger (refus, erreur). */
  const handleStripeProcessing = useCallback((processing: boolean): void => {
    setPaymentRedirectPending(processing);
    setStripeProcessing(processing);
  }, []);
  useEffect((): (() => void) => (): void => setPaymentRedirectPending(false), []);

  /* ── Conditions annoncées avant la pré-réservation : le bouton unique en découle ──
     Résumé du panier en échec : ses conditions seraient périmées, la pré-réservation tranche (bug 11). */
  const checkoutPreview = codesApi.summaryError ? undefined : cartSummary?.checkout;
  const flow = resolveCheckoutFlow(checkoutPreview, payChoice);
  const showPayChoice = offersPayChoice(checkoutPreview);
  /* « Sur place » n'est un choix que s'il a été proposé : un choix fait avant qu'une carte cadeau couvre tout
     ne compte plus (le serveur annonce alors 0, rien n'est réglé sur place). */
  const payOnSiteChosen = showPayChoice && payChoice === 'on_site';
  /* Confirmation en un clic (gratuit, sur place, demande) : l'engagement d'annulation, s'il y a lieu, se coche avant. */
  const oneClick = flow === 'confirm' || flow === 'request';
  /* Validation par le pro (`confirm_every_booking`, annoncée par `cart-summary.checkout`) : mention au-dessus du
     bouton dans tous les modes de paiement (spec Parcours § 7.6, cas 14). */
  const requiresValidation = !!checkoutPreview?.requires_validation;
  const previewValidationNotice = requiresValidation
    ? validationNotice(
        flow === 'pay_online' &&
          chargesOnlineNow(checkoutPreview?.payment_type, checkoutPreview?.amount_to_pay_today_raw),
        labels,
      )
    : undefined;
  const primaryLabel = (flow: CheckoutFlow | undefined): string =>
    flow === 'pay_online'
      ? labels.continue_to_payment
      : flow === 'request'
        ? labels.send_request
        : flow === 'confirm'
          ? labels.confirm_appointment
          : labels.continue;

  /* ── Derived state (après la pré-réservation) ── */
  const isPreReserved = !!preReservationUuid;
  const paymentType = preReservationData?.payment_type;
  const isOptional = paymentType === 'FULL_PAYMENT_OPTIONAL';
  const isCardOrImprint = paymentType === 'CARD_SAVE' || paymentType === 'BANK_IMPRINT';
  const amountToPayTodayRaw = preReservationData?.payment_infos?.amount_to_pay_today_raw ?? 0;
  // Coupon (carte cadeau) : une remise de code promo n'engage rien à l'annulation (cf. PaymentTimeline).
  const hasVoucherDiscount =
    preReservationData?.discount_source !== 'promo' &&
    hasNonZeroFormattedAmount(preReservationData?.payment_infos?.discount_amount);
  const isNoOnlinePaymentRequired = preReservationData ? needsNoOnlinePayment(preReservationData) : false;
  /* Paiement au choix réglé sur place : la confirmation « sur place » du serveur. Couvert à 100 % (carte cadeau,
     bon, code), rien n'est dû : confirmation sans paiement, comme un acompte couvert — le choix n'a plus d'objet. */
  const confirmsOnSite = isOptional && !isNoOnlinePaymentRequired;
  const isFree = isNoOnlinePaymentRequired && !hasVoucherDiscount;
  const paymentInfos = preReservationData?.payment_infos;
  const preReservationAmounts = preReservationData?.amounts;
  /* Montant du jour (le détail est dans le récap) : à payer en ligne, ou rien quand la carte cadeau couvre
     ce qui est dû aujourd'hui ; le reste se règle sur place. */
  const amountToPayTodayDisplay = preReservationAmounts?.amount_to_pay_today ?? paymentInfos?.amount_to_pay_today;
  const remainingOnSiteValue = preReservationAmounts
    ? preReservationAmounts.remaining_on_site_raw
      ? preReservationAmounts.remaining_on_site
      : undefined
    : paymentInfos?.remaining_amount_on_site;
  const remainingOnSiteDisplay = hasNonZeroFormattedAmount(remainingOnSiteValue) ? remainingOnSiteValue : undefined;
  // Empreinte ou carte enregistrée : rien n'est dû aujourd'hui de toute façon (cf. `card_save_info`).
  const isCoveredByGiftCard =
    !isCardOrImprint && amountToPayTodayRaw === 0 && (preReservationAmounts?.voucher_lines?.length ?? 0) > 0;
  /* Panier gratuit : rien à remiser, pas de champ code (sauf pour retirer un code encore posé). */
  const showInlineCodeField = codesApi.amounts?.gross_total_raw !== 0 || !!appliedCodes.promo || !!appliedCodes.voucher;
  // Montant « plancher » (QUOTE/FROM ou NONE mêlé à des prestas chiffrées) → préfixe « à partir de ».
  const hasLowerBoundPricing = useMemo((): boolean => cartHasLowerBoundPricing(cartItems), [cartItems]);

  /* ── Codes (champ du récap) : figés pendant la création de la pré-réservation et le paiement ── */
  const { setLocked, setCustomerContext, registerRecreate, busy: codeBusy, revision: codesRevision } = codesApi;
  useEffect((): (() => void) => {
    setLocked(submitting || busy);
    return (): void => setLocked(false);
  }, [submitting, busy, setLocked]);

  /* Email et téléphone saisis, une fois valides : un code promo posé depuis le récap est tranché avec eux
     (code personnel, nouveaux clients, plafond par client). Incomplets, ils ne partent pas. */
  useEffect((): void => {
    const email = form.email.trim();
    const phone = form.phone.trim();
    setCustomerContext({
      ...(EMAIL_RE.test(email) ? { email } : {}),
      ...(isValidPhone(phone) ? { phone } : {}),
    });
  }, [form.email, form.phone, setCustomerContext]);

  /* ── Form handlers ─────────────────────────── */

  const updateField = useCallback(
    (field: keyof CheckoutCustomerDraft, value: string): void => {
      setForm((prev): CheckoutCustomerDraft => ({ ...prev, [field]: value }));
      if (touchedFields.has(field)) {
        setFormErrors((prev): CustomerInfoErrors => {
          const updated = { ...prev };
          delete updated[field as keyof CustomerInfoErrors];
          return updated;
        });
      }
    },
    [touchedFields],
  );

  const handleBlur = useCallback(
    (field: keyof CheckoutCustomerDraft): void => {
      setTouchedFields((prev): Set<string> => new Set(prev).add(field));
      const errors = validateCustomerInfo(form, labels);
      if (errors[field as keyof CustomerInfoErrors]) {
        setFormErrors(
          (prev): CustomerInfoErrors => ({
            ...prev,
            [field as keyof CustomerInfoErrors]: errors[field as keyof CustomerInfoErrors],
          }),
        );
      }
    },
    [form, labels],
  );

  /* ── Build pre-reservation slot payload ─────── */

  const slotPayload = useMemo((): PreReserveSlotPayload[] => {
    if (!selectedSlot) return [];
    return cartItems.map((item, i): PreReserveSlotPayload => {
      const assignment = memberAssignments.find((a): boolean => a.cartIndex === i);
      const resAssignment = resourceAssignments.find((a): boolean => a.cartIndex === i);
      const subSlot = selectedSlot.slots?.[i];

      const explicitMemberIds = assignment?.memberIds?.filter((id): boolean => id !== undefined);
      const hasExplicitMulti = (explicitMemberIds?.length ?? 0) > 0;

      return {
        slot_start: subSlot?.slot_start ?? selectedSlot.slot_start,
        slot_end: subSlot?.slot_end ?? selectedSlot.slot_end,
        /* "Sans préférence" → undefined, backend auto-assigns an available member */
        team_member_id: hasExplicitMulti ? explicitMemberIds?.[0] : assignment?.memberId,
        team_member_ids: hasExplicitMulti ? explicitMemberIds : undefined,
        pro_place_id: selectedPlaceId ?? 0,
        prestation_id: item.prestation.prestation_id,
        quantity: item.quantity,
        selected_price_index: item.selectedPriceIndex,
        ...toSupplementSelectionPayload(item),
        selected_resource_id: resAssignment?.pro_resource_id,
        party_size: item.party_size,
      };
    });
  }, [cartItems, memberAssignments, resourceAssignments, selectedPlaceId, selectedSlot]);

  const createPreReservation = useCallback(
    (codes: AppliedCodes): Promise<PreReservationResponse> => {
      if (!callbacks.onPreReserve) return Promise.reject(new Error(labels.error_generic));
      return callbacks.onPreReserve({
        slots: slotPayload,
        customer: {
          firstname: form.firstname.trim(),
          lastname: form.lastname.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
        },
        note: form.note.trim() || undefined,
        // Un code promo et une carte cadeau peuvent partir ensemble (cumul).
        voucher_code: codes.voucher,
        promo_code: codes.promo,
        customer_address: ctx.customerAddress,
        custom_field_answers: customAnswersPayload.length > 0 ? customAnswersPayload : undefined,
      });
    },
    [callbacks, slotPayload, form, ctx.customerAddress, customAnswersPayload, labels.error_generic],
  );

  /* Pré-réservation devenue non modifiable (intent Stripe réglé ou figé) : annulée puis recréée avec les
     codes voulus, depuis le champ code du récap. */
  const recreatePreReservationWithCodes = useCallback(
    async (codes: AppliedCodes): Promise<boolean> => {
      if (!callbacks.onPreReserve) return false;

      if (preReservationUuid && callbacks.onCancelPreReservation) {
        try {
          await callbacks.onCancelPreReservation(preReservationUuid);
        } catch {
          // Best effort: if cancellation fails, we still try to create a new pre-reservation.
        }
      }

      const response = await createPreReservation(codes);
      send({ type: 'PRE_RESERVATION_CREATED', data: response });
      setPaymentRefreshToken((prev): number => prev + 1);
      return true;
    },
    [callbacks, preReservationUuid, createPreReservation, send],
  );
  useEffect((): (() => void) => {
    registerRecreate(recreatePreReservationWithCodes);
    return (): void => registerRecreate(undefined);
  }, [recreatePreReservationWithCodes, registerRecreate]);

  /* ── Confirmations sans paiement en ligne ──── */

  const confirmWith = useCallback(
    async (
      uuid: string,
      confirm: ((uuid: string) => Promise<{ success: boolean; message?: string }>) | undefined,
    ): Promise<void> => {
      setConfirming(true);
      try {
        if (confirm) {
          const result = await confirm(uuid);
          if (!result.success) {
            setSubmitError(result.message ?? labels.error_generic);
            return;
          }
        }
        send({ type: 'PAYMENT_CONFIRMED', reservationUuid: uuid });
      } catch (err: unknown) {
        setSubmitError(err instanceof Error ? err.message : labels.error_generic);
      } finally {
        setConfirming(false);
      }
    },
    [send, labels.error_generic],
  );

  /* ── Submit customer info → pre-reservation (puis confirmation si rien n'est payé en ligne) ── */

  const handleSubmitInfo = useCallback(async (): Promise<void> => {
    if (codeBusy) return;
    const errors = validateCustomerInfo(form, labels);
    setFormErrors(errors);
    setTouchedFields(new Set(['firstname', 'lastname', 'email', 'phone']));

    if (hasCustomerInfoErrors(errors)) return;
    if (!validateCustomFields()) return;
    if (!callbacks.onPreReserve) return;

    /* Persist customer info in machine context so it survives re-mounts */
    send({ type: 'SAVE_CUSTOMER_INFO', info: form });

    // Champ code figé tout de suite : la pré-réservation part avec les codes posés à cet instant.
    setLocked(true);
    setSubmitting(true);
    setSubmitError(undefined);

    let response: PreReservationResponse;
    try {
      response = await createPreReservation(appliedCodes);
    } catch (err: unknown) {
      setSubmitting(false);
      const errorKey = getErrorKey(err);
      if (errorKey && SLOT_LOST_ERRORS.has(errorKey)) {
        send({ type: 'SLOT_TAKEN' });
        return;
      }
      if (appliedCodes.promo && errorKey?.startsWith('PRE_RESERVATION_PROMO_')) {
        // Code promo refusé à la création (conditions tranchées sur le créneau et le client) : il ne
        // reste ni affiché comme appliqué, ni déduit du total ; le client lit pourquoi et peut réserver.
        send({
          type: 'CODES_CHANGED',
          codes: appliedCodes.voucher ? { voucher: appliedCodes.voucher } : {},
        });
        setSubmitError(resolvePromoErrorMessage(errorKey, getUserMessage(err), labels));
        return;
      }
      setSubmitError(err instanceof Error ? err.message : labels.error_generic);
      return;
    }
    setSubmitting(false);
    send({ type: 'PRE_RESERVATION_CREATED', data: response });

    /* Un seul clic : rien à payer en ligne (gratuit, couvert par une carte cadeau — paiement au choix compris —,
       demande au pro) ⇒ confirmé tout de suite ; sur place choisi ⇒ confirmé sur place. Un serveur qui demande
       finalement un paiement laisse le bloc paiement s'afficher. */
    if (oneClick && needsNoOnlinePayment(response)) {
      await confirmWith(response.reservation_uuid, callbacks.onConfirmReservation);
    } else if (oneClick && payOnSiteChosen && response.payment_type === 'FULL_PAYMENT_OPTIONAL') {
      await confirmWith(response.reservation_uuid, callbacks.onConfirmPayOnSiteReservation);
    }
  }, [
    codeBusy,
    form,
    labels,
    validateCustomFields,
    callbacks,
    setLocked,
    createPreReservation,
    appliedCodes,
    send,
    payOnSiteChosen,
    oneClick,
    confirmWith,
  ]);

  /* ── Timer expired ──────────────────────────── */

  const handleTimerExpired = useCallback((): void => {
    send({ type: 'TIMER_EXPIRED' });
  }, [send]);

  /* ── Coordonnées à modifier : la pré-réservation est libérée, le formulaire se rouvre ── */
  const handleEditCustomer = useCallback((): void => {
    if (preReservationUuid) callbacks.onCancelPreReservation?.(preReservationUuid).catch((): void => undefined);
    send({ type: 'PRE_RESERVATION_RELEASED' });
    setCancellationAccepted(false);
  }, [preReservationUuid, callbacks, send]);

  /* ── Navigation possédée par le shell ─────────
   * Avant la pré-réservation : le bouton unique vit dans le footer/sidebar (libellé selon le cas).
   * Pré-réservé : le paiement rend ses propres boutons (Stripe / sur place / gratuit) → `ownsPrimary`.
   * Le retour libère la pré-réservation (BookingContext). */
  const handlePrimary = useCallback((): void => {
    void handleSubmitInfo();
  }, [handleSubmitInfo]);

  /* Engagement d'annulation des confirmations en un clic, coché avant le bouton (sinon après la pré-réservation), et
     seulement quand une pénalité existe (spec Parcours § 7.6) : rien n'y est payé ni garanti en ligne, seule une carte
     cadeau ou un bon engagé se perd à une annulation tardive. */
  const commitmentText = checkoutPreview?.cancellation_deadline_formatted
    ? `${labels.cancellation_until_no_payment} ${checkoutPreview.cancellation_deadline_formatted}`
    : labels.cancellation_attendance_commitment;
  const needsCommitmentFirst = oneClick && hasCancellationPenalty({ hasVoucher: !!appliedCodes.voucher });
  const primaryBlocked = codeBusy || confirming || (needsCommitmentFirst && !cancellationAccepted);

  useRegisterStepNav(
    useMemo(
      (): StepNav =>
        isPreReserved
          ? { onBack: goBack, ownsPrimary: true }
          : {
              onBack: goBack,
              primary: {
                label: primaryLabel(flow),
                onClick: handlePrimary,
                loading: submitting || confirming,
                disabled: primaryBlocked,
              },
            },
      [isPreReserved, goBack, flow, handlePrimary, submitting, confirming, primaryBlocked, labels],
    ),
  );

  /* ── Render ────────────────────────────────── */

  /* Case d'engagement après la pré-réservation : seulement quand une pénalité existe (frais d'annulation tardive
     annoncés par le serveur, délai déjà dépassé compris, ou bon engagé). */
  const showCommitment =
    isPreReserved &&
    hasCancellationPenalty({
      cancellationFeeOutRaw: paymentInfos?.cancellation_fee_out_time_raw,
      hasVoucher: hasVoucherDiscount || (preReservationAmounts?.voucher_lines?.length ?? 0) > 0,
    });
  const cancellationNode = showCommitment ? (
    <CancellationCommitment
      accepted={cancellationAccepted}
      onChange={setCancellationAccepted}
      isFree={isFree}
      deadlineIso={preReservationData?.cancellation_deadline}
      deadlineFormatted={String(preReservationData?.cancellation_deadline_formatted ?? '')}
    />
  ) : null;
  const cancellationBlocks = showCommitment && !cancellationAccepted;
  /* Bouton du paiement : « Payer … » pour un débit, une confirmation pour une empreinte ou une carte enregistrée. */
  const payLabel = paymentButtonLabel(
    {
      paymentType,
      amountToPay: amountToPayTodayDisplay,
      amountToPayRaw: preReservationAmounts?.amount_to_pay_today_raw ?? paymentInfos?.amount_to_pay_today_raw,
      requiresValidation,
    },
    labels,
  );
  const paymentValidationNotice = requiresValidation
    ? validationNotice(
        chargesOnlineNow(
          paymentType,
          preReservationAmounts?.amount_to_pay_today_raw ?? paymentInfos?.amount_to_pay_today_raw,
        ),
        labels,
      )
    : undefined;
  const customerSummary = [`${form.firstname} ${form.lastname}`.trim(), form.email, form.phone]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{labels.checkout_title}</h2>
      </div>

      <div className="bt-checkout">
        {isPreReserved ? (
          /* Coordonnées repliées : « modifier » libère le créneau et rouvre le formulaire. */
          <div className="bt-checkout__section bt-checkout__customer-summary">
            <span className="bt-checkout__customer-summary-text">{customerSummary}</span>
            <button
              type="button"
              className="bt-link-subtle"
              aria-label={labels.customer_summary_edit}
              onClick={handleEditCustomer}
              disabled={busy}
            >
              {labels.edit}
            </button>
          </div>
        ) : (
          <>
            {/* ── Customer info section ──────────────── */}
            <div className="bt-checkout__section">
              <h3 className="bt-checkout__section-title">{labels.your_info}</h3>
              {promotionPending && <p className="bt-checkout__promotion-notice">{promotionPending}</p>}
              <CustomerInfoFields
                values={form}
                errors={formErrors}
                onChange={updateField}
                onBlur={handleBlur}
                locale={locale}
                flagBaseUrl={flagBaseUrl}
                idPrefix="bt"
                labels={{
                  firstname: labels.firstname,
                  lastname: labels.lastname,
                  email: labels.email,
                  phone: labels.phone,
                  phone_country_code: labels.phone_country_code,
                  phone_placeholder: labels.phone_placeholder,
                }}
              />

              <div className="bt-field bt-field--note">
                <label className="bt-field__label" htmlFor="bt-note">
                  {labels.note_label}
                </label>
                <textarea
                  id="bt-note"
                  className="bt-field__input bt-field__input--textarea"
                  value={form.note}
                  onChange={(e): void => updateField('note', e.target.value)}
                  placeholder={labels.note_placeholder}
                  rows={3}
                />
              </div>
            </div>
          </>
        )}

        {/* ── Dynamic custom fields ──────────────── */}
        <DynamicFormFields
          fields={visibleCustomFields}
          answers={customAnswers}
          onUpdateAnswer={updateCustomAnswer}
          errors={customErrors}
          onBlurField={handleCustomFieldBlur}
          sectionLabel={labels.additional_info}
        />

        {/* ── Champ code, en colonne unique seulement : en layout wide il vit dans la sidebar récap.
            Même état que celui du récap (BookingCodesContext), figé pendant la création de la
            pré-réservation et le paiement. ── */}
        {showInlineCodeField && (
          <div className="bt-checkout__section bt-checkout__code-inline">
            <BookingCodeField />
          </div>
        )}

        {!isPreReserved && (
          <>
            {/* ── Paiement au choix du client, choisi AVANT la pré-réservation ── */}
            {showPayChoice && checkoutPreview ? (
              <fieldset className="bt-checkout__section bt-checkout__pay-choice">
                <legend className="bt-checkout__section-title">{labels.pay_choice_title}</legend>
                <label className="bt-field__radio-label">
                  <input
                    type="radio"
                    name="bt-pay-choice"
                    checked={payChoice === 'online'}
                    onChange={(): void => setPayChoice('online')}
                  />
                  <span>{labels.pay_online_option.replace('{amount}', checkoutPreview.amount_to_pay_today)}</span>
                </label>
                <label className="bt-field__radio-label">
                  <input
                    type="radio"
                    name="bt-pay-choice"
                    checked={payChoice === 'on_site'}
                    onChange={(): void => setPayChoice('on_site')}
                  />
                  <span>{labels.pay_on_site_option}</span>
                </label>
              </fieldset>
            ) : null}

            {/* ── Sur place ou gratuit : ce qui se règle au rendez-vous ── */}
            {flow === 'confirm' && checkoutPreview && checkoutPreview.remaining_on_site_raw > 0 && !showPayChoice ? (
              <div className="bt-checkout__pay-today">
                <span>{labels.to_pay_on_site}</span>
                <span className="bt-checkout__pay-today-amount">
                  {withFromPrefix(checkoutPreview.remaining_on_site, hasLowerBoundPricing, labels.from_price_prefix)}
                </span>
              </div>
            ) : null}

            {previewValidationNotice ? <p className="bt-checkout__info-text">{previewValidationNotice}</p> : null}

            {/* ── Annulation : engagement coché avant un clic qui confirme, simple rappel avant un paiement ── */}
            {needsCommitmentFirst ? (
              <div className="bt-checkout__cancellation">
                <label className="bt-field__checkbox-label">
                  <input
                    type="checkbox"
                    className="bt-field__checkbox"
                    checked={cancellationAccepted}
                    onChange={(e): void => setCancellationAccepted(e.target.checked)}
                  />
                  <span>{commitmentText}</span>
                </label>
              </div>
            ) : flow === 'pay_online' && checkoutPreview?.cancellation_deadline_formatted ? (
              <p className="bt-checkout__info-text">
                {labels.cancellation_free_until_date.replace('{date}', checkoutPreview.cancellation_deadline_formatted)}
              </p>
            ) : null}

            {submitError && (
              <p className="bt-error" role="alert" ref={submitErrorRef}>
                {submitError}
              </p>
            )}
          </>
        )}

        {/* ── Payment section (after pre-reservation) ───── */}
        {isPreReserved && (
          <>
            {promotionRejected && (
              <p className="bt-checkout__promotion-rejected" role="status">
                {promotionRejected}
              </p>
            )}
            {preReservationData?.expires_at && (
              <CountdownTimer
                expiresAt={preReservationData.expires_at}
                onExpired={handleTimerExpired}
                reservedLabel={labels.countdown_reserved_for_you}
                expiredLabel={labels.countdown_expired}
                rechooseLabel={labels.countdown_rechoose}
                onRechoose={handleTimerExpired}
              />
            )}

            {/* Payment recap */}
            {paymentInfos && (
              <div className="bt-checkout__section">
                <h3 className="bt-checkout__section-title">{labels.payment_section}</h3>

                {isCardOrImprint ? <p className="bt-checkout__info-text">{labels.card_save_info}</p> : null}

                {/* Montant du jour : le détail (remise, carte, total) est dans le récap. */}
                <div className="bt-checkout__pay-today">
                  <span>{isCoveredByGiftCard ? labels.pay_today_covered : labels.amount_to_pay}</span>
                  <span className="bt-checkout__pay-today-amount">{amountToPayTodayDisplay}</span>
                </div>
                {remainingOnSiteDisplay && (
                  <p className="bt-checkout__info-text">
                    {labels.remaining_on_site_sentence.replace(
                      '{amount}',
                      withFromPrefix(remainingOnSiteDisplay, hasLowerBoundPricing, labels.from_price_prefix),
                    )}
                  </p>
                )}
              </div>
            )}

            {submitError && (
              <p className="bt-error" role="alert" ref={submitErrorRef}>
                {submitError}
              </p>
            )}

            <BookingPaymentTimeline />
            {isNoOnlinePaymentRequired || (confirmsOnSite && payOnSiteChosen) ? (
              /* Confirmation restée à faire (engagement non coché avant, ou serveur différent de l'annonce). */
              <>
                {requiresValidation ? (
                  <p className="bt-checkout__info-text">{validationNotice(false, labels)}</p>
                ) : null}
                {cancellationNode}
                <button
                  type="button"
                  className="bt-cta bt-cta--mt"
                  onClick={(): void => {
                    if (!preReservationUuid) return;
                    void confirmWith(
                      preReservationUuid,
                      confirmsOnSite ? callbacks.onConfirmPayOnSiteReservation : callbacks.onConfirmReservation,
                    );
                  }}
                  disabled={confirming || cancellationBlocks}
                >
                  {confirming ? (
                    <span className="bt-spinner bt-spinner--inline" />
                  ) : flow === 'request' ? (
                    labels.send_request
                  ) : (
                    labels.confirm_appointment
                  )}
                </button>
              </>
            ) : (
              <BookingPaymentSection
                key={`${preReservationUuid ?? 'pre-reservation'}-${paymentRefreshToken}-${codesRevision}`}
                payLabel={payLabel}
                disabled={cancellationBlocks}
                busy={busy}
                onProcessingChange={handleStripeProcessing}
                extraContent={
                  <>
                    {/* Validation par le pro : la mention juste au-dessus du bouton de paiement. */}
                    {paymentValidationNotice ? (
                      <p className="bt-checkout__info-text">{paymentValidationNotice}</p>
                    ) : null}
                    {cancellationNode}
                  </>
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* Conditions d'annulation à accepter avant de payer (ou de confirmer, quand l'engagement n'a pas été pris avant). */
function CancellationCommitment({
  accepted,
  onChange,
  isFree,
  deadlineIso,
  deadlineFormatted,
}: {
  accepted: boolean;
  onChange: (accepted: boolean) => void;
  isFree: boolean;
  /** Absent : l'annulation gratuite est déjà passée (rendez-vous du jour), la pénalité vaut dès maintenant. */
  deadlineIso: string | undefined;
  deadlineFormatted: string;
}): JSX.Element {
  const { labels } = useBooking();
  const deadlinePassed = !deadlineIso || new Date(deadlineIso).getTime() <= Date.now();
  return (
    <div className="bt-checkout__cancellation">
      <label className="bt-field__checkbox-label">
        <input
          type="checkbox"
          className="bt-field__checkbox"
          checked={accepted}
          onChange={(e): void => onChange(e.target.checked)}
        />
        <span>
          {isFree ? (
            deadlinePassed ? (
              <>{labels.cancellation_attendance_commitment}</>
            ) : (
              <>
                {labels.cancellation_until_no_payment} {deadlineFormatted}
              </>
            )
          ) : (
            <>
              {labels.cancellation_accept}
              <br />
              <span className="bt-checkout__cancellation-detail">
                {deadlinePassed ? (
                  <>{labels.cancellation_no_refund_after_confirm}</>
                ) : (
                  <>
                    {labels.cancellation_free_until} {deadlineFormatted}
                  </>
                )}
              </span>
            </>
          )}
        </span>
      </label>
    </div>
  );
}
